import React, { useEffect, useRef, useState } from 'react';
import cn from 'classnames';
import { toast } from '@leadsnavi/ui-kit';
import { useMemoizedFn } from 'ahooks';
import mixpanel from 'mixpanel-browser';
import type { CompanySource, PlatformCompany, SearchCardV2SubMsg } from '../../../types';
import type {
  IApiAliIntlCompanyItem,
  IApiCompanyItem,
  IApiGlobalCompanyItem,
  IApiMadeInChinaCompanyItem,
} from '@/services/swagger/easourcing/models';
import { CompanyCardPortal } from './CompanyCardPortal';
import type { CompanyRowDisplayFlags } from './types';
import { SearchCardV2SkeletonItem } from './SearchCardV2SkeletonItem';
import { SupplierCardActions } from '../recommended-supplier-card/components/SupplierCardActions';
import { CompBlockProps } from '@/features/chat-messages/types/comp-block';
import { useSearchCardPolling } from '@/features/chat-messages/hooks/useSearchCardPolling';
import { useChatPageStore } from '@/features/chat-messages/store/chat-store';
import { modifySearchCardPost, searchResultCardStatusGet } from '@/services/swagger/easourcing/SessionService';
import type { IApiModifySearchCardReq, IApiSubMessageDto } from '@/services/swagger/easourcing/models';
import { ProcurementRequestConfirmationCard } from './ProcurementRequestConfirmationCard';

/**
 * 将 ALI_INTL 平台公司数据适配为统一的 PlatformCompany 格式
 */
function adaptAliIntlCompany(raw: IApiAliIntlCompanyItem): PlatformCompany {
  return {
    searchResultId: raw.searchResultId,
    companyName: raw.companyName ?? null,
    logoUrl: raw.logoUrl ?? null,
    countryCode: raw.countryCode ?? null,
    tagList: raw.tagList?.length > 0 ? raw.tagList : null,
    mainProducts: raw.mainProducts?.length > 0 ? raw.mainProducts : null,
    certList: raw.certList?.length > 0 ? raw.certList : null,
    onTimeDelivery: raw.onTimeDelivery ?? null,
    replyAvgTime: raw.replyAvgTime ?? null,
    productList: raw.productList?.length > 0 ? raw.productList : null,
    action: raw.action ?? null,
    aiInsight: null,
  };
}

/**
 * 将 MADE_IN_CHINA 平台公司数据适配为统一的 PlatformCompany 格式
 */
function adaptMadeInChinaCompany(raw: IApiMadeInChinaCompanyItem): PlatformCompany {
  return {
    searchResultId: raw.searchResultId,
    companyName: raw.companyName ?? null,
    logoUrl: raw.logoUrl ?? null,
    countryCode: raw.countryCode ?? null,
    tagList: raw.tagList?.length > 0 ? raw.tagList : null,
    mainProducts: raw.mainProducts?.length > 0 ? raw.mainProducts : null,
    certList: raw.certList?.length > 0 ? raw.certList : null,
    onTimeDelivery: raw.onTimeDelivery ?? null,
    replyAvgTime: raw.replyAvgTime ?? null,
    productList: raw.productList?.length > 0 ? raw.productList : null,
    action: raw.action ?? null,
    aiInsight: null,
  };
}

/**
 * 将 WMT 全球搜索数据适配为统一的 PlatformCompany 格式
 *
 * 字段映射规则：
 * - name         → companyName
 * - logo         → logoUrl
 * - standardCountry（全名）→ countryCode（ISO 二字母，通过映射表转换）
 * - industries   → tagList
 * - recommendReason → aiInsight（推荐理由）
 * - companyId    → companyId（WMT 唯一标识，用于 React key）
 */
function adaptWmtCompany(raw: IApiGlobalCompanyItem): PlatformCompany {
  return {
    // WMT 无标准 searchResultId，用 0 兜底（实际不做精度运算）
    searchResultId: 0,
    companyId: raw.companyId ?? null,
    companyName: raw.name ?? null,
    logoUrl: raw.logo ?? null,
    countryCode: raw.standardCountry ?? null,
    tagList: raw.industries ? [raw.industries] : null,
    mainProducts: raw.recProductList ? [raw.recProductList] : null,
    certList: null,
    onTimeDelivery: null,
    replyAvgTime: null,
    productList: null,
    action: null,
    aiInsight: raw.refinedSellingPoint ?? null,
    allTotalTransactions: raw.allTotalTransactions ?? null,
    allValueOfGoodsUSDDouble: raw.allValueOfGoodsUSDDouble ?? null,
    allLastTransactionDate: raw.allLastTransactionDate ?? null,
  };
}

/**
 * 根据 IApiCompanyItem.source 将三种平台子对象统一适配为前端通用的 PlatformCompany 类型
 * - ALI_INTL       → adaptAliIntlCompany
 * - MADE_IN_CHINA  → adaptMadeInChinaCompany
 * - WMT            → adaptWmtCompany（字段结构不同，需映射转换）
 * - GLOBAL         → adaptWmtCompany（globalCompany 结构与 WMT 相同）
 */
function getCompany(item: IApiCompanyItem): PlatformCompany | null {
  if (item.source === 'ALI_INTL') {
    return item.aliIntlCompany ? adaptAliIntlCompany(item.aliIntlCompany) : null;
  }
  if (item.source === 'MADE_IN_CHINA') {
    return item.madeInChinaCompany ? adaptMadeInChinaCompany(item.madeInChinaCompany) : null;
  }
  // WMT 与 GLOBAL 均走 globalCompany 字段，结构与 IApiGlobalCompanyItem 相同
  const raw = item.globalCompany as unknown as IApiGlobalCompanyItem | null;
  return raw ? adaptWmtCompany(raw) : null;
}

/**
 * 骨架态检测：getCompany 返回 null 或 companyName 为 null 时视为骨架态
 */
function isSkeletonItem(item: IApiCompanyItem): boolean {
  const company = getCompany(item);
  return !company || company.companyName === null;
}

/**
 * 从 IApiCompanyItem 取出 source 用于 CompanyCard 路由
 * WMT 数据经 adaptWmtCompany 适配后与 GLOBAL 结构一致，统一走 GlobalCompanyCard 渲染
 */
function getSource(item: IApiCompanyItem): CompanySource {
  if (item.source === 'WMT') return 'GLOBAL';
  return item.source as CompanySource;
}

// ─── 行分组显示标志计算 ──────────────────────────────────────

/**
 * 对同一行的卡片（最多2张）计算 CompanyRowDisplayFlags
 * - 入参为已适配的 PlatformCompany 列表（非骨架态，即 companyName 非 null）
 * - 只要该行中任意一张卡片有此字段数据，对应区块在整行都显示
 * - 若所有卡片均无此字段，则整行统一隐藏该区块
 */
function computeRowFlags(row: PlatformCompany[]): CompanyRowDisplayFlags {
  const showPriceSection = row.some(c => Boolean(c.productList?.[0]?.price));
  const showStatsSection = row.some(c => Boolean(c.onTimeDelivery || c.replyAvgTime));
  const showProductSection = row.some(c => Boolean(c.productList?.length || c.mainProducts?.length));
  const showTagsSection = row.some(c => Boolean(c.tagList && c.tagList.length > 0));
  const showInsightSection = row.some(c => Boolean(c.aiInsight));
  return {
    showCertSection: true,
    showPriceSection,
    showStatsSection,
    showProductSection,
    showTagsSection,
    showInsightSection,
  };
}

// ─── 主组件 ──────────────────────────────────────────────────

export type SearchCardV2CardProps = Pick<CompBlockProps, 'sessionId'> & {
  subMsg: SearchCardV2SubMsg;
  /** 外层消息 ID，用于 modify-search-card 轮询接口及 store 更新 */
  messageId?: string | number;
  className?: string;
};

export function SearchCardV2Card({ subMsg, className, sessionId, messageId }: SearchCardV2CardProps) {
  const { condition, companyList, buyerProfile } = subMsg.contentValue || {};
  const queryProduct = condition?.queryProduct || '';
  const subMessageId = subMsg.subMessageId ?? '';

  const updateSearchCardSubMsg = useChatPageStore(s => s.updateSearchCardSubMsg);

  // 搜索卡片出现埋点：挂载时触发一次
  useEffect(() => {
    mixpanel.track('searchcard_dispare', {
      request_id: subMessageId,
      request_name: queryProduct,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── 轮询 Hook ──────────────────────────────────────────────
  const { isPolling, startPolling } = useSearchCardPolling({
    messageId: messageId ?? '',
    sessionId: sessionId ?? '',
    subMessageId,
    onComplete: resp => {
      if (!resp.cardData || !messageId) return;
      // 将后端返回的 cardData 构造成 submessage 格式并替换到 store
      // cardData 结构与 SearchCardV2SubMsg.contentValue 对齐
      const newSubMsg: IApiSubMessageDto = {
        ...(subMsg as unknown as IApiSubMessageDto),
        contentValue: {
          ...resp.cardData,
        },
      };
      updateSearchCardSubMsg(String(messageId), subMessageId, newSubMsg);
    },
    onError: useMemoizedFn((message: string) => {
      toast.error(message);
    }),
  });

  // ─── 刷新恢复：挂载时检测 status（US2）────────────────────────
  useEffect(() => {
    if (!messageId || !sessionId || !subMessageId) return;

    searchResultCardStatusGet(
      {
        messageId: messageId as unknown as number,
        sessionId: sessionId as unknown as number,
        subMessageId,
      },
      { skipGlobalErrorHandling: true } as Record<string, unknown>
    )
      .then(result => {
        if (result?.data?.cardData?.status === 0) {
          startPolling();
        }
      })
      .catch(() => {
        // 静默处理：挂载时检测失败不影响正常展示
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // 仅挂载时执行一次

  // ─── 监听 isPolling 从 true → false，递增 key ────────────────
  const prevIsPollingRef = useRef(isPolling);
  const [actionKey, setActionKey] = useState(0);
  useEffect(() => {
    if (prevIsPollingRef.current === true && isPolling === false) {
      setActionKey(k => k + 1);
    }
    prevIsPollingRef.current = isPolling;
  }, [isPolling]);

  // ─── Modify 提交处理（US1）────────────────────────────────────
  const handleModifySubmit = useMemoizedFn(async (req: IApiModifySearchCardReq) => {
    try {
      await modifySearchCardPost(req);
      startPolling();
    } catch (error: unknown) {
      const apiError = error as { message?: string };
      toast.error(apiError?.message || 'Failed to modify search card');
    }
  });

  return (
    <div>
      <ProcurementRequestConfirmationCard condition={condition} buyerProfile={buyerProfile} className=" mb-4" />

      <div className={cn('bg-[#F5F5F5] rounded-xl p-5 flex flex-col gap-4', className)}>
        {/* 标题区 */}
        <div className="flex flex-col gap-1">
          <h2 className="text-xl font-semibold text-[#262626] [font-family:Geist]">
            {queryProduct} Recommended Suppliers
          </h2>
          <div className="flex flex-col gap-1 text-xs font-normal text-[#262626] leading-[150%] tracking-[0.015em]">
            <span>Here are some companies that match your criteria.</span>
            <span>
              Like what you see? Hit <span className="font-medium">&ldquo;Send Inquiry by Agent&rdquo;</span> to connect
              with more like these.
            </span>
            <span>
              Want to fine-tune? Hit <span className="font-medium">&ldquo;Modify the Inquiry&rdquo;</span> to adjust
              your search.
            </span>
          </div>
        </div>

        {/* 供应商列表 */}
        {!companyList || companyList.length === 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <SearchCardV2SkeletonItem key={i} />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {(() => {
              const nodes: React.ReactNode[] = [];
              for (let i = 0; i < companyList.length; i += 2) {
                const row = companyList.slice(i, i + 2);
                // 将行内非骨架项预先适配为 PlatformCompany，供 computeRowFlags 使用
                const rowCompanies = row.flatMap(item => {
                  const c = getCompany(item);
                  return c && c.companyName !== null ? [c] : [];
                });
                const flags = computeRowFlags(rowCompanies);
                row.forEach((item, offset) => {
                  const idx = i + offset;
                  if (isSkeletonItem(item)) {
                    nodes.push(<SearchCardV2SkeletonItem key={idx} />);
                    return;
                  }
                  const company = getCompany(item);
                  if (!company) return;
                  // WMT 卡片 searchResultId 固定为 0，使用 companyId 或列表索引作为唯一 key
                  const cardKey =
                    item.source === 'WMT' ? (company.companyId ?? `wmt-${i + offset}`) : String(company.searchResultId);
                  nodes.push(
                    <CompanyCardPortal key={cardKey} company={company} source={getSource(item)} displayFlags={flags} />
                  );
                });
              }
              return nodes;
            })()}
          </div>
        )}
        <SupplierCardActions
          key={actionKey}
          subMsg={subMsg}
          sessionId={sessionId}
          messageId={messageId}
          isModifying={isPolling}
          onModifySubmit={handleModifySubmit}
        />
      </div>
    </div>
  );
}
